import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';
import { tasksApi } from '../services/api';

interface TaskProgressBarProps {
  taskType: string;
  startTime: number;
  progress?: number; // Добавляем поддержку реального прогресса
  className?: string;
}

const TaskProgressBar: React.FC<TaskProgressBarProps> = ({ taskType, startTime, progress, className = '' }) => {
  const [averageTimeMinutes, setAverageTimeMinutes] = useState<number | null>(null);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [elapsedMinutes, setElapsedMinutes] = useState(0);
  const [estimatedRemainingMinutes, setEstimatedRemainingMinutes] = useState<number | null>(null);

  // Загружаем среднее время выполнения при монтировании компонента
  useEffect(() => {
    const fetchAverageTime = async () => {
      try {
        const response = await tasksApi.getAverageExecutionTime(taskType);
        if (response.data.hasData && response.data.averageTimeMinutes) {
          setAverageTimeMinutes(response.data.averageTimeMinutes);
        } else {
          // Если нет данных, используем средние значения по умолчанию
          const defaultTimes: Record<string, number> = {
            'ContentPlan': 3,
            'Roadmap': 2,
            'Reels': 5,
            'SingleReels': 4,
            'Audience': 3,
            'Summary': 2
          };
          setAverageTimeMinutes(defaultTimes[taskType] || 3);
        }
      } catch (error) {
        console.error('Ошибка при получении среднего времени выполнения:', error);
        // Fallback значения
        const defaultTimes: Record<string, number> = {
          'ContentPlan': 3,
          'Roadmap': 2,
          'Reels': 5,
          'SingleReels': 4,
          'Audience': 3,
          'Summary': 2
        };
        setAverageTimeMinutes(defaultTimes[taskType] || 3);
      }
    };

    fetchAverageTime();
  }, [taskType]);

  // Обновляем прогресс каждую секунду
  useEffect(() => {
    if (!averageTimeMinutes) return;

    // Валидируем startTime
    if (isNaN(startTime) || startTime <= 0) {
      return;
    }

    const updateProgress = () => {
      const now = Date.now();
      const elapsed = (now - startTime) / 1000 / 60; // в минутах
      
      // Если есть реальный прогресс от сервера (>0), используем его
      let calculatedProgress: number;
      if (typeof progress === 'number' && progress > 0 && progress <= 100) {
        calculatedProgress = progress;
      } else {
        // Иначе рассчитываем на основе времени
        calculatedProgress = (elapsed / averageTimeMinutes) * 100;
      }

      // Нормализуем: от 2% до 95% для UX (оставляем место на завершение)
      const normalized = Math.max(2, Math.min(calculatedProgress, 95));
      
      setElapsedMinutes(Math.floor(elapsed));
      setCurrentProgress(normalized);
      
      const remaining = Math.max(0, averageTimeMinutes - elapsed);
      setEstimatedRemainingMinutes(Math.ceil(remaining));
    };

    updateProgress(); // Обновляем сразу
    const interval = setInterval(updateProgress, 1000);

    return () => clearInterval(interval);
  }, [startTime, averageTimeMinutes, progress]);

  if (!averageTimeMinutes) {
    return null; // Загружается...
  }

  const formatTime = (totalMinutes: number): string => {
    if (totalMinutes < 0.017) { // меньше 1 секунды
      return 'меньше секунды';
    }
    
    const minutes = Math.floor(totalMinutes);
    const seconds = Math.round((totalMinutes - minutes) * 60);
    
    if (minutes === 0) {
      if (seconds === 1) {
        return '1 секунда';
      } else if (seconds < 5) {
        return `${seconds} секунды`;
      } else {
        return `${seconds} секунд`;
      }
    }
    
    if (seconds === 0) {
      if (minutes === 1) {
        return '1 минута';
      } else if (minutes < 5) {
        return `${minutes} минуты`;
      } else {
        return `${minutes} минут`;
      }
    }
    
    // Формат "X минут Y секунд"
    let result = '';
    
    if (minutes === 1) {
      result += '1 минута';
    } else if (minutes < 5) {
      result += `${minutes} минуты`;
    } else {
      result += `${minutes} минут`;
    }
    
    result += ' ';
    
    if (seconds === 1) {
      result += '1 секунда';
    } else if (seconds < 5) {
      result += `${seconds} секунды`;
    } else {
      result += `${seconds} секунд`;
    }
    
    return result;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Информация о времени */}
      <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
        <div className="flex items-center space-x-1">
          <Clock className="w-3 h-3" />
          <span>Осталось ~ {formatTime(estimatedRemainingMinutes || 0)}</span>
        </div>
      </div>

      {/* Прогресс-бар */}
      <div className="relative">
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-visible">
          <motion.div
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full relative"
            initial={{ width: 0 }}
            animate={{ width: `${currentProgress}%` }}
            transition={{ duration: 1, ease: "linear" }}
          >
            {/* Анимированный круг в конце полосы */}
            <motion.div
              className="absolute top-1/2 -right-2 w-4 h-4 rounded-full bg-indigo-600 dark:bg-indigo-400 border-2 border-white dark:border-gray-800 shadow-lg"
              style={{ y: '-50%' }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.8, 1, 0.8]
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TaskProgressBar; 